import { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Box,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Divider,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import DecimalField from '../shared/DecimalField';
import { DEFAULT_VAT_RATE, calcPriceInclVat, calcPriceExclVat, formatPrice } from '../../utils/vat';
import { useUserStore } from '../../store';
import VatModeToggle from '../shared/VatModeToggle';
import VatConverseLine from '../shared/VatConverseLine';

function QuickAddDialog({ open, handleClose, type, maintenance, setMaintenance }) {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();
  const orgDefaultVat = currentOrganization?.default_price_includes_vat ?? false;
  const showPurchasePrice = type === 'part' && (currentOrganization?.quick_add_fields || []).includes('purchase_price');
  const catalogueEnabled = type === 'part' && (currentOrganization?.vendor_catalogues_enabled ?? false);

  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [price, setPrice] = useState('');
  const [priceIncludesVat, setPriceIncludesVat] = useState(orgDefaultVat);
  const [purchasePrice, setPurchasePrice] = useState('');
  const [purchasePriceIncludesVat, setPurchasePriceIncludesVat] = useState(orgDefaultVat);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Catalogue code lookup (tuoteluettelot) — optional and skippable
  const [catalogueResults, setCatalogueResults] = useState(null);
  const [catalogueLoading, setCatalogueLoading] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName('');
      setQuantity('1');
      setPrice('');
      setPriceIncludesVat(orgDefaultVat);
      setPurchasePrice('');
      setPurchasePriceIncludesVat(orgDefaultVat);
      setError(null);
      setCatalogueResults(null);
    }
  }, [open, orgDefaultVat]);

  const handleCatalogueLookup = async () => {
    const query = name.trim();
    if (query.length < 3) return;
    setCatalogueLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/catalogue-search/', { params: { q: query } });
      setCatalogueResults(response.data.results || []);
    } catch {
      setCatalogueResults([]);
    } finally {
      setCatalogueLoading(false);
    }
  };

  // A blank or unparseable quantity field means one piece
  const resolvedQuantity = () => parseFloat(quantity) || 1;

  const handleCatalogueSelect = async (row) => {
    setSubmitting(true);
    setError(null);
    try {
      const materializeResponse = await axios.post(`/api/catalogue-items/${row.item_id}/materialize/`);
      const response = await axios.patch(`/api/maintenances/${maintenance.id}/add_part/`, {
        part_id: materializeResponse.data.part.id,
        quantity: resolvedQuantity(),
      });
      setMaintenance(response.data);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.detail || t('catalogues.materializeFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;

    setSubmitting(true);
    setError(null);

    try {
      // A lone "-" parses to NaN -> treat as not entered
      const parsedPrice = parseFloat(price);
      const enteredPrice = Number.isFinite(parsedPrice) ? parsedPrice : null;

      if (type === 'part') {
        // Calculate VAT-exclusive sell price if entered with VAT
        let sellPriceExclVat = enteredPrice;
        if (enteredPrice !== null && priceIncludesVat) {
          sellPriceExclVat = calcPriceExclVat(enteredPrice, DEFAULT_VAT_RATE);
        }

        // Calculate purchase price if enabled and entered
        let purchasePriceExclVat = null;
        let storedPurchasePriceInclVat = null;
        let purchasePriceEnteredWithVat = false;
        if (showPurchasePrice && purchasePrice) {
          const enteredPurchasePrice = parseFloat(purchasePrice);
          if (purchasePriceIncludesVat) {
            purchasePriceExclVat = calcPriceExclVat(enteredPurchasePrice, DEFAULT_VAT_RATE);
            storedPurchasePriceInclVat = Math.round(enteredPurchasePrice * 100) / 100;
            purchasePriceEnteredWithVat = enteredPurchasePrice > 0;
          } else {
            purchasePriceExclVat = enteredPurchasePrice;
          }
        }

        // Step 1: Create part (soft-deleted via hide_from_registry)
        const createResponse = await axios.post('/api/parts/', {
          name: name.trim(),
          sell_price: sellPriceExclVat,
          stored_sell_price_incl_vat: priceIncludesVat && enteredPrice !== null ? Math.round(enteredPrice * 100) / 100 : null,
          sell_price_entered_with_vat: priceIncludesVat && enteredPrice !== null && enteredPrice !== 0,
          price: purchasePriceExclVat,
          stored_price_incl_vat: storedPurchasePriceInclVat,
          price_entered_with_vat: purchasePriceEnteredWithVat,
          vat_rate: DEFAULT_VAT_RATE,
          source_type: 'manual',
          hide_from_registry: true,
        });

        // Step 2: Add to maintenance
        const response = await axios.patch(`/api/maintenances/${maintenance.id}/add_part/`, {
          part_id: createResponse.data.id,
          quantity: resolvedQuantity(),
        });
        setMaintenance(response.data);
      } else {
        // Job flow
        let priceExclVat = enteredPrice;
        if (enteredPrice !== null && priceIncludesVat) {
          priceExclVat = calcPriceExclVat(enteredPrice, DEFAULT_VAT_RATE);
        }

        // Step 1: Create job (soft-deleted via hide_from_registry)
        const createResponse = await axios.post('/api/jobs/', {
          name: name.trim(),
          price: priceExclVat ?? 0,
          vat_rate: DEFAULT_VAT_RATE,
          stored_price_incl_vat: priceIncludesVat && enteredPrice !== null ? Math.round(enteredPrice * 100) / 100 : null,
          price_entered_with_vat: priceIncludesVat && enteredPrice !== null && enteredPrice !== 0,
          price_type: 'fixed',
          hide_from_registry: true,
        });

        // Step 2: Add to maintenance
        const response = await axios.patch(`/api/maintenances/${maintenance.id}/add_job/`, {
          job_id: createResponse.data.id,
          quantity: 1,
        });
        setMaintenance(response.data);
      }

      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const priceLabel = type === 'part'
    ? (priceIncludesVat ? t('Sell price (incl. VAT)') : t('Sell price (excl. VAT)'))
    : (priceIncludesVat ? t('Price (incl. VAT)') : t('Price (excl. VAT)'));

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: '12px', sm: '16px' },
          m: { xs: 2, sm: 4 },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography
          sx={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 600,
            fontSize: '18px',
          }}
        >
          {t('Quick add')} – {type === 'part' ? t('Part') : t('Job')}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2, fontSize: '13px' }}>
          {t('quickAddExplanation')}
        </Alert>

        <TextField
          label={t('Name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
          autoFocus
          InputProps={
            catalogueEnabled
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={t('catalogues.quickLookup')}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={handleCatalogueLookup}
                            disabled={name.trim().length < 3 || catalogueLoading}
                            data-testid="quick-add-catalogue-lookup"
                          >
                            {catalogueLoading ? <CircularProgress size={16} /> : <ManageSearchIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }
              : undefined
          }
          sx={{
            mb: catalogueResults ? 1 : 2,
            '& .MuiOutlinedInput-root': { borderRadius: '10px' },
          }}
        />

        {catalogueEnabled && catalogueResults && (
          <Box sx={{ mb: 2 }}>
            {catalogueResults.length === 0 ? (
              <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                {t('catalogues.noMatches')}
              </Typography>
            ) : (
              catalogueResults.slice(0, 5).map((row) => (
                <Box
                  key={row.item_id}
                  onClick={() => !submitting && handleCatalogueSelect(row)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    mb: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  data-testid={`quick-add-catalogue-result-${row.item_id}`}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.name || row.source_code}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                      {row.vendor_name}
                      {row.brand ? ` · ${row.brand}` : ''} · {row.source_code}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {/* Catalogue price is ex-VAT; respect the org's VAT-inclusive display setting. */}
                    {orgDefaultVat ? formatPrice(calcPriceInclVat(row.purchase_price, DEFAULT_VAT_RATE)) : formatPrice(row.purchase_price)} €
                    <Typography component="span" sx={{ fontSize: '10px', fontWeight: 400, color: 'text.secondary', ml: 0.5 }}>
                      ({orgDefaultVat ? t('incl. VAT') : t('excl. VAT')})
                    </Typography>
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        )}

        {type === 'part' && (
          <DecimalField
            label={t('Quantity')}
            value={quantity}
            onChange={(value) => setQuantity(value)}
            placeholder="1"
            inputProps={{ 'data-testid': 'quick-add-quantity' }}
            sx={{
              mb: 2,
              width: { xs: '100%', sm: '120px' },
              '& .MuiOutlinedInput-root': { borderRadius: '10px' },
            }}
          />
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DecimalField
            label={priceLabel}
            value={price}
            onChange={(value) => setPrice(value)}
            allowNegative
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">€</InputAdornment>,
            }}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '10px' },
            }}
          />
          <VatModeToggle
            value={priceIncludesVat ? 'incl' : 'excl'}
            onChange={(e, val) => val && setPriceIncludesVat(val === 'incl')}
            sx={{ flexShrink: 0 }}
          />
        </Box>

        <VatConverseLine
          value={price}
          mode={priceIncludesVat ? 'incl' : 'excl'}
          vatRate={DEFAULT_VAT_RATE}
          sx={{ mt: 0.5 }}
        />

        {showPurchasePrice && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <DecimalField
                label={purchasePriceIncludesVat ? t('Purchase price (incl. VAT)') : t('Purchase price (excl. VAT)')}
                value={purchasePrice}
                onChange={(value) => setPurchasePrice(value)}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                }}
              />
              <VatModeToggle
                value={purchasePriceIncludesVat ? 'incl' : 'excl'}
                onChange={(e, val) => val && setPurchasePriceIncludesVat(val === 'incl')}
                sx={{ flexShrink: 0 }}
                help={false}
              />
            </Box>

            <VatConverseLine
              value={purchasePrice}
              mode={purchasePriceIncludesVat ? 'incl' : 'excl'}
              vatRate={DEFAULT_VAT_RATE}
              sx={{ mt: 0.5 }}
            />
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ borderRadius: '10px' }}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!name.trim() || submitting}
          sx={{
            borderRadius: '10px',
            bgcolor: 'secondary.main',
            '&:hover': { bgcolor: 'secondary.dark' },
          }}
        >
          {submitting ? <CircularProgress size={20} color="inherit" /> : t('Create and add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default QuickAddDialog;
